import { useState, useEffect } from 'react';
import { Link, useNavigate } from 'react-router-dom';
import { motion, AnimatePresence } from 'framer-motion';
import { useAuth } from '../../contexts/AuthContext';
import {
  Plus,
  Search,
  Filter,
  MapPin,
  DollarSign,
  Calendar,
  Users,
  MoreVertical,
  Eye,
  Edit,
  Trash2,
  ArrowRight
} from 'lucide-react';
import { toast } from 'sonner';

const statusConfig = {
  completed: { label: 'Completed', color: 'bg-green-500/10 text-green-600 border-green-200' },
  active: { label: 'Active', color: 'bg-blue-500/10 text-blue-600 border-blue-200' },
  pursuing: { label: 'Pursuing', color: 'bg-yellow-500/10 text-yellow-600 border-yellow-200' },
  bid_submitted: { label: 'Bid Submitted', color: 'bg-purple-500/10 text-purple-600 border-purple-200' },
  walkthrough_scheduled: { label: 'Walkthrough', color: 'bg-indigo-500/10 text-indigo-600 border-indigo-200' },
  on_hold: { label: 'On Hold', color: 'bg-gray-500/10 text-gray-600 border-gray-200' },
  lost: { label: 'Lost', color: 'bg-red-500/10 text-red-600 border-red-200' }
};

export const JobsPipeline = () => {
  const { api } = useAuth();
  const navigate = useNavigate();
  const [jobs, setJobs] = useState([]);
  const [loading, setLoading] = useState(true);
  const [searchQuery, setSearchQuery] = useState('');
  const [statusFilter, setStatusFilter] = useState('all');
  const [viewMode, setViewMode] = useState('grid'); // grid or list

  useEffect(() => {
    fetchJobs();
  }, []);

  const fetchJobs = async () => {
    try {
      const response = await api.get('/jobs');
      setJobs(response.data);
    } catch (error) {
      console.error('Error fetching jobs:', error);
      toast.error('Failed to load jobs');
    } finally {
      setLoading(false);
    }
  };

  const filteredJobs = jobs.filter(job => {
    const matchesSearch = job.client_name.toLowerCase().includes(searchQuery.toLowerCase()) ||
                          job.address.toLowerCase().includes(searchQuery.toLowerCase()) ||
                          job.project_type.toLowerCase().includes(searchQuery.toLowerCase());
    const matchesStatus = statusFilter === 'all' || job.status === statusFilter;
    return matchesSearch && matchesStatus;
  });

  const groupedJobs = {
    active: filteredJobs.filter(j => j.status === 'active'),
    pursuing: filteredJobs.filter(j => ['pursuing', 'bid_submitted', 'walkthrough_scheduled'].includes(j.status)),
    completed: filteredJobs.filter(j => j.status === 'completed'),
    other: filteredJobs.filter(j => ['on_hold', 'lost'].includes(j.status))
  };

  const deleteJob = async (jobId) => {
    if (!window.confirm('Are you sure you want to delete this job?')) return;
    try {
      await api.delete(`/jobs/${jobId}`);
      setJobs(jobs.filter(j => j.id !== jobId));
      toast.success('Job deleted');
    } catch (error) {
      toast.error('Failed to delete job');
    }
  };

  if (loading) {
    return (
      <div className="flex items-center justify-center h-64">
        <div className="w-8 h-8 border-2 border-[#C6A87C] border-t-transparent rounded-full animate-spin" />
      </div>
    );
  }

  const JobCard = ({ job }) => (
    <motion.div
      layout
      initial={{ opacity: 0, scale: 0.95 }}
      animate={{ opacity: 1, scale: 1 }}
      exit={{ opacity: 0, scale: 0.95 }}
      className="bg-white border border-gray-100 p-5 hover:shadow-lg transition-shadow group"
    >
      <div className="flex justify-between items-start mb-4">
        <span className={`px-3 py-1 text-xs font-semibold border ${statusConfig[job.status]?.color}`}>
          {statusConfig[job.status]?.label}
        </span>
        <div className="relative">
          <button className="p-1 hover:bg-gray-100 rounded opacity-0 group-hover:opacity-100 transition-opacity">
            <MoreVertical className="w-4 h-4 text-gray-400" />
          </button>
        </div>
      </div>

      <h3 className="font-heading text-lg mb-1">{job.client_name}</h3>
      <p className="text-sm text-gray-500 mb-4">{job.project_type}</p>

      <div className="space-y-2 text-sm">
        <div className="flex items-center gap-2 text-gray-500">
          <MapPin className="w-4 h-4" />
          <span>{job.address}, {job.city}</span>
        </div>
        <div className="flex items-center gap-2 text-gray-500">
          <DollarSign className="w-4 h-4" />
          <span>${job.estimated_value?.toLocaleString()}</span>
        </div>
        {job.start_date && (
          <div className="flex items-center gap-2 text-gray-500">
            <Calendar className="w-4 h-4" />
            <span>{new Date(job.start_date).toLocaleDateString()}</span>
          </div>
        )}
      </div>

      {/* Progress bar for active jobs */}
      {job.status === 'active' && (
        <div className="mt-4">
          <div className="flex justify-between text-xs text-gray-500 mb-1">
            <span>Progress</span>
            <span>{Math.round(job.completion_percentage)}%</span>
          </div>
          <div className="h-2 bg-gray-100 rounded-full overflow-hidden">
            <div
              className="h-full bg-[#C6A87C] transition-all duration-500"
              style={{ width: `${job.completion_percentage}%` }}
            />
          </div>
        </div>
      )}

      <div className="flex gap-2 mt-4 pt-4 border-t border-gray-100">
        <Link
          to={`/portal/jobs/${job.id}`}
          className="flex-1 flex items-center justify-center gap-2 py-2 text-xs font-semibold text-gray-600 hover:text-[#C6A87C] transition-colors"
        >
          <Eye className="w-4 h-4" />
          View
        </Link>
        <button
          onClick={() => navigate(`/portal/jobs/${job.id}/edit`)}
          className="flex-1 flex items-center justify-center gap-2 py-2 text-xs font-semibold text-gray-600 hover:text-[#C6A87C] transition-colors"
        >
          <Edit className="w-4 h-4" />
          Edit
        </button>
        <button
          onClick={() => deleteJob(job.id)}
          className="flex items-center justify-center gap-2 py-2 px-3 text-xs font-semibold text-gray-400 hover:text-red-500 transition-colors"
        >
          <Trash2 className="w-4 h-4" />
        </button>
      </div>
    </motion.div>
  );

  return (
    <div className="space-y-6" data-testid="jobs-pipeline">
      {/* Header */}
      <div className="flex flex-col md:flex-row justify-between items-start md:items-center gap-4">
        <div>
          <h1 className="font-heading text-3xl">Jobs Pipeline</h1>
          <p className="text-gray-500 mt-1">{jobs.length} total jobs</p>
        </div>
        <Link
          to="/portal/jobs/new"
          className="inline-flex items-center gap-2 bg-[#C6A87C] text-white px-6 py-3 text-xs font-semibold uppercase tracking-widest hover:bg-[#B09265] transition-colors"
        >
          <Plus className="w-4 h-4" />
          New Job
        </Link>
      </div>

      {/* Filters */}
      <div className="flex flex-col md:flex-row gap-4">
        <div className="relative flex-1">
          <Search className="absolute left-4 top-1/2 -translate-y-1/2 w-5 h-5 text-gray-400" />
          <input
            type="text"
            placeholder="Search jobs..."
            value={searchQuery}
            onChange={(e) => setSearchQuery(e.target.value)}
            className="w-full pl-12 pr-4 py-3 border border-gray-200 focus:border-[#C6A87C] focus:outline-none"
            data-testid="job-search"
          />
        </div>
        <select
          value={statusFilter}
          onChange={(e) => setStatusFilter(e.target.value)}
          className="px-4 py-3 border border-gray-200 focus:border-[#C6A87C] focus:outline-none bg-white min-w-[180px]"
          data-testid="status-filter"
        >
          <option value="all">All Statuses</option>
          {Object.entries(statusConfig).map(([key, { label }]) => (
            <option key={key} value={key}>{label}</option>
          ))}
        </select>
      </div>

      {/* Stats Summary */}
      <div className="grid grid-cols-2 md:grid-cols-4 gap-4">
        <div className="bg-blue-50 border border-blue-100 p-4">
          <p className="text-xs font-semibold uppercase text-blue-600 mb-1">Active</p>
          <p className="font-heading text-2xl text-blue-700">{groupedJobs.active.length}</p>
        </div>
        <div className="bg-yellow-50 border border-yellow-100 p-4">
          <p className="text-xs font-semibold uppercase text-yellow-600 mb-1">Pipeline</p>
          <p className="font-heading text-2xl text-yellow-700">{groupedJobs.pursuing.length}</p>
        </div>
        <div className="bg-green-50 border border-green-100 p-4">
          <p className="text-xs font-semibold uppercase text-green-600 mb-1">Completed</p>
          <p className="font-heading text-2xl text-green-700">{groupedJobs.completed.length}</p>
        </div>
        <div className="bg-[#C6A87C]/10 border border-[#C6A87C]/20 p-4">
          <p className="text-xs font-semibold uppercase text-[#C6A87C] mb-1">Total Value</p>
          <p className="font-heading text-2xl text-[#B09265]">
            ${filteredJobs.reduce((sum, j) => sum + (j.estimated_value || 0), 0).toLocaleString()}
          </p>
        </div>
      </div>

      {/* Jobs Grid */}
      {filteredJobs.length > 0 ? (
        <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-6">
          <AnimatePresence mode="popLayout">
            {filteredJobs.map(job => (
              <JobCard key={job.id} job={job} />
            ))}
          </AnimatePresence>
        </div>
      ) : (
        <div className="text-center py-16 bg-gray-50">
          <p className="text-gray-400 mb-4">No jobs found</p>
          <Link
            to="/portal/jobs/new"
            className="inline-flex items-center gap-2 text-[#C6A87C] font-semibold hover:underline"
          >
            Create your first job <ArrowRight className="w-4 h-4" />
          </Link>
        </div>
      )}
    </div>
  );
};
